import axios from 'axios';
import keycloak from '../config/keycloak';

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    if (keycloak.token) {
      config.headers.Authorization = `Bearer ${keycloak.token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and token is expired, try to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshed = await keycloak.updateToken(30);
        if (refreshed) {
          originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        keycloak.login();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  updateProfile: (data) => api.put('/auth/update-profile', data),
  deleteMe: (password) => api.delete('/auth/me', { data: { password } }),
  changePassword: (currentPassword, newPassword) => api.put('/auth/change-password', { currentPassword, newPassword }),
  register: () => api.post('/auth/register'),
  completeOnboarding: (data) => api.post('/auth/complete-onboarding', data),
};

// Config API (public endpoints)
export const configAPI = {
  getSupportedCountries: () => api.get('/config/supported-countries'),
};

// Health API (public endpoints for monitoring)
export const healthAPI = {
  getReclaimStatus: () => api.get('/health/reclaim'),
};

// Admin API
export const adminAPI = {
  getSupportedCountries: () => api.get('/admin/supported-countries'),
  addSupportedCountry: (code) => api.post('/admin/supported-countries', { code }),
  removeSupportedCountry: (code) => api.delete(`/admin/supported-countries/${code}`),
  grantPremium: (userId, data) => api.post(`/admin/users/${userId}/grant-premium`, data),
  revokePremium: (userId) => api.post(`/admin/users/${userId}/revoke-premium`),
  // Reclaim feature management
  getReclaimStatus: () => api.get('/admin/reclaim/status'),
  toggleReclaim: (enabled) => api.put('/admin/reclaim/toggle', { enabled }),
};

// CGM API
export const cgmAPI = {
  getTypes: () => api.get('/cgm/types'),
  getCredentials: () => api.get('/cgm/credentials'),
  addCredentials: (data) => api.post('/cgm/credentials', data),
  updateCredentials: (id, data) => api.put(`/cgm/credentials/${id}`, data),
  deleteCredentials: (id) => api.delete(`/cgm/credentials/${id}`),
  getGlucose: () => api.get('/cgm/glucose'), // Generic endpoint that routes to correct provider
};

// Dexcom API
export const dexcomAPI = {
  getGlucose: () => api.get('/dexcom/glucose'),
};

// Nightscout API
export const nightscoutAPI = {
  getGlucose: () => api.get('/nightscout/glucose'),
};

// Profile API
export const profileAPI = {
  getProfiles: () => api.get('/profile/list'),
  getProfile: (id) => api.get(`/profile/${id}`),
  getCurrentTimeBlock: (id) => api.get(`/profile/${id}/current`),
  getActiveProfileCurrent: () => api.get('/profile/active/current'),
  getChangelog: (id, params) => api.get(`/profile/${id}/changelog`, { params }),
  getActivationHistory: (params) => api.get('/profile/activation-history', { params }),
  getProfileActivationHistory: (id, params) => api.get(`/profile/${id}/activation-history`, { params }),
  createProfile: (data) => api.post('/profile', data),
  updateProfile: (id, data) => api.put(`/profile/${id}`, data),
  activateProfile: (id) => api.post(`/profile/${id}/activate`),
  archiveProfile: (id) => api.post(`/profile/${id}/archive`),
  unarchiveProfile: (id) => api.post(`/profile/${id}/unarchive`),
  duplicateProfile: (profileId, newName) => api.post(`/profile/${profileId}/duplicate`, { newName }),
  deleteProfile: (id) => api.delete(`/profile/${id}`),
};

// IOB API
export const iobAPI = {
  calculateIOB: (params) => api.get('/iob/calculate', { params }),
};

// Sensors API
export const sensorsAPI = {
  getSensorTypes: () => api.get('/sensors/types'),
  getSensors: (params) => api.get('/sensors', { params }),
  getActiveSensor: () => api.get('/sensors/active'),
  getSensor: (id) => api.get(`/sensors/${id}`),
  createSensor: (data) => api.post('/sensors', data),
  updateSensor: (id, data) => api.put(`/sensors/${id}`, data),
  endSensor: (id, data) => api.put(`/sensors/${id}/end`, data),
  deleteSensor: (id) => api.delete(`/sensors/${id}`),
};

// Reclaim API (sensor reclaim via Rubin Medical)
export const reclaimAPI = {
  getProfile: () => api.get('/reclaim/profile'),
  updateProfile: (data) => api.put('/reclaim/profile', data),
  getErrorTypes: (sensorType) => api.get('/reclaim/error-types', { params: { sensorType } }),
  submitReclaim: (sensorId, data) => api.post(`/reclaim/${sensorId}/reclaim`, data),
  getHistory: (sensorId) => api.get(`/reclaim/${sensorId}/reclaim-history`),
};

// Thresholds API
export const thresholdsAPI = {
  getThresholds: () => api.get('/thresholds'),
  updateThresholds: (data) => api.put('/thresholds', data),
};

// Ketones API
export const ketonesAPI = {
  getKetones: (params) => api.get('/ketones', { params }),
  getKetone: (id) => api.get(`/ketones/${id}`),
  createKetone: (data) => api.post('/ketones', data),
  updateKetone: (id, data) => api.put(`/ketones/${id}`, data),
  deleteKetone: (id) => api.delete(`/ketones/${id}`),
};

// HbA1c API
export const hba1cAPI = {
  getReadings: (params) => api.get('/hba1c', { params }),
  getReading: (id) => api.get(`/hba1c/${id}`),
  createReading: (data) => api.post('/hba1c', data),
  updateReading: (id, data) => api.put(`/hba1c/${id}`, data),
  deleteReading: (id) => api.delete(`/hba1c/${id}`),
};

// Manual Glucose API
export const manualGlucoseAPI = {
  getReadings: (params) => api.get('/manual-glucose', { params }),
  getReading: (id) => api.get(`/manual-glucose/${id}`),
  createReading: (data) => api.post('/manual-glucose', data),
  updateReading: (id, data) => api.put(`/manual-glucose/${id}`, data),
  deleteReading: (id) => api.delete(`/manual-glucose/${id}`),
};

// Glucose Data API (for graph view)
export const glucoseDataAPI = {
  getReadings: (params) => api.get('/glucose-data', { params }),
};

// Meals API
export const mealsAPI = {
  getSavedMeals: () => api.get('/meals/saved'),
  getSavedMeal: (id) => api.get(`/meals/saved/${id}`),
  createSavedMeal: (data) => api.post('/meals/saved', data),
  updateSavedMeal: (id, data) => api.put(`/meals/saved/${id}`, data),
  deleteSavedMeal: (id) => api.delete(`/meals/saved/${id}`),
  getHistory: (params) => api.get('/meals/history', { params }),
  getHistoryItem: (id) => api.get(`/meals/history/${id}`),
  createHistoryItem: (data) => api.post('/meals/history', data),
  updateHistoryItem: (id, data) => api.put(`/meals/history/${id}`, data),
  deleteHistoryItem: (id) => api.delete(`/meals/history/${id}`),
  getSettings: () => api.get('/meals/settings'),
  updateSettings: (data) => api.put('/meals/settings', data),
};

// Subscription API
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  createPortalSession: () => api.post('/subscription/create-portal-session'),
};

// Pricing API
export const pricingAPI = {
  getPricing: (currency = 'SEK') => api.get('/pricing', { params: { currency } }),
};

// Admin Pricing API
export const adminPricingAPI = {
  listPricing: () => api.get('/admin/pricing'),
  // data: { currency, interval, amount_cents, trial_days, is_active, description }
  createPricing: (data) => api.post('/admin/pricing', data),
  // currency: 'SEK', interval: 'year', data: { amount_cents, trial_days, is_active }
  updatePricing: (currency, interval, data) => api.put(`/admin/pricing/${currency}/${interval}`, data),
  refreshCache: () => api.post('/pricing/refresh'),
};

// Reports API
export const reportsAPI = {
  // For regular users (own report)
  generateMedicalReport: (params) => api.post('/reports/generate', params),
  exportMedicalReportPdf: (params) => api.get('/reports/pdf', {
    params,
    responseType: 'blob'
  }),
  // For admins (any user's report)
  generateUserReport: (userId, params) => api.post(`/reports/generate/${userId}`, params),
  exportUserReportPdf: (userId, params) => api.get(`/reports/pdf/${userId}`, {
    params,
    responseType: 'blob'
  }),
};

// Food API (Private/Personal Food Items - Premium Feature)
export const foodAPI = {
  // Get user's personal food items (Personliga)
  getMyItems: () => api.get('/food/my-items'),
  // Add a new private food item
  addPrivateItem: (data) => api.post('/food/private', data),
  // Update a private food item
  updatePrivateItem: (id, data) => api.put(`/food/private/${id}`, data),
  // Delete a private food item
  deletePrivateItem: (id) => api.delete(`/food/private/${id}`),
  // Check for similar existing items
  checkSimilar: (name) => api.get('/food/similar', { params: { name } }),
};

// Meal Photo API (AI Carb Analysis - Premium Feature)
export const mealPhotoAPI = {
  // Analyze a meal photo
  analyze: (image, notes) => api.post('/meal-photo/analyze', { image, notes }),
  // Get daily usage stats
  getUsage: () => api.get('/meal-photo/usage'),
  // Get analysis history (30 days)
  getHistory: (params) => api.get('/meal-photo/history', { params }),
  // Get image URL for a specific analysis
  getImageUrl: (id) => `/api/meal-photo/image/${id}`,
  // Vote on an analysis
  vote: (id, vote, comment) => api.post(`/meal-photo/${id}/vote`, { vote, comment }),
  // Get user's vote for an analysis
  getVote: (id) => api.get(`/meal-photo/${id}/vote`),
};

// AI Prompts Admin API
export const aiPromptsAPI = {
  get: (type) => api.get(`/admin/ai-prompts/${type}`),
  update: (type, data) => api.put(`/admin/ai-prompts/${type}`, data),
};

// Admin Meal Votes API
export const adminMealVotesAPI = {
  list: (params) => api.get('/admin/meal-votes', { params }),
  stats: () => api.get('/admin/meal-votes/stats'),
};

export default api;
